/**
 * The status vocabulary of a finance entry, built like the agenda's
 * (`src/pages/schedulings/components/status-config.ts`): a semantic token, a
 * soft chip ground, a coloured edge, a dot and a label. Status is shown with
 * colour *and* shape, never colour alone (design-system.md §2).
 *
 * The API sends `status` as an appended attribute of the entry:
 *   `dd/mm/yyyy` — the entry is paid, on that day
 *   `delayed`    — the entry is open and its maturity has passed
 *   `null`       — the entry is open and not due yet
 */

export type EntryStatusKind = "paid" | "overdue" | "open";

export interface EntryStatusConfig {
  kind: EntryStatusKind;
  /** The semantic token of the status, as a CSS custom property name. */
  token: string;
  /** Soft chip ground + neutral text; the dot and the edge carry the colour. */
  chip: string;
  edge: string;
  dot: string;
  label: string;
}

const PAID_DATE = /^(\d{2})\/(\d{2})\/(\d{4})$/;

export function isPaidStatus(status: string | null | undefined): boolean {
  return typeof status === "string" && PAID_DATE.test(status);
}

/** Whole days between the maturity day and today, both read as calendar days. */
export function daysOverdue(maturity: string, now: Date): number {
  const due = new Date(maturity);
  if (Number.isNaN(due.getTime())) return 0;

  const dueDay = Date.UTC(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate());
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());

  return Math.round((today - dueDay) / 86_400_000);
}

function overdueLabel(days: number): string {
  if (days <= 0) return "Vencido hoje";
  if (days === 1) return "Vencido há 1 dia";
  return `Vencido há ${days} dias`;
}

export function getEntryStatusConfig(
  status: string | null | undefined,
  maturity: string,
  now: Date = new Date()
): EntryStatusConfig {
  if (isPaidStatus(status)) {
    return {
      kind: "paid",
      token: "--app-success",
      chip: "bg-app-success/10 text-app-fg-strong",
      edge: "border-l-app-success",
      dot: "bg-app-success",
      label: `Pago em ${status}`,
    };
  }

  if (status === "delayed") {
    return {
      kind: "overdue",
      token: "--app-danger",
      chip: "bg-app-danger/10 text-app-fg-strong",
      edge: "border-l-app-danger",
      dot: "bg-app-danger",
      label: overdueLabel(daysOverdue(maturity, now)),
    };
  }

  return {
    kind: "open",
    token: "--app-info",
    chip: "bg-app-info/10 text-app-fg-strong",
    edge: "border-l-app-info",
    dot: "bg-app-info",
    label: "Em aberto",
  };
}
